import './PersonBlock.scss'

import {ImageType, WysiwygType} from '../../types'

import {Background} from '@codeurs/front'
import {Block} from './Blocks'
import {Link} from '../../util/link'
import React from 'react'
import {Theme} from '../Theme'
import {style} from '../../util/style'
import {useTranslation} from '../../util/i18n'

const styles = style('personblock', ['items', 'item'])

type PersonBlockChannels = 'contact_persons_item' | 'contact_groups_item'
type PersonBlockTypes = 'person' | 'group' | 'custom'
type PersonBlockItemType = {
	image?: ImageType
	name?: string
	title?: string
	text?: WysiwygType
	email?: string
	phone?: string
	collaborator?: PersonBlockCollaborator
}
export type PersonBlockCollaborator = {
	url: string
	title: string
	image?: ImageType
	text?: WysiwygType
	email?: string
	phone?: string
}

export type PersonBlockType = PersonBlockItemType & {
	channel?: PersonBlockChannels
	persons?: Array<PersonBlockItemType>
}

const getPersonBlockType = (channel: PersonBlockChannels): PersonBlockTypes => {
	switch (channel) {
		case 'contact_persons_item':
			return 'person'
		case 'contact_groups_item':
			return 'group'
		default:
			return 'custom'
	}
}
export const PersonBlock: React.FC<
	PersonBlockType & {
		container?: boolean
		fullwidth?: boolean
		sidebar?: boolean
	}
> = ({...props}) => {
	const {
		channel = null,
		container,
		fullwidth,
		sidebar,
		name,
		persons = []
	} = props

	const personBlockType = getPersonBlockType(channel)
	const hasPersons = persons && persons.length > 0

	if (!hasPersons && personBlockType === 'group') return null
	if (!hasPersons && !props.name) return null

	const singleOrMultiplePersons =
		hasPersons && personBlockType === 'group' ? (
			<div className={styles.items()}>
				{persons.map((person, i) => (
					<PersonBlockItem key={i} {...person} />
				))}
			</div>
		) : (
			<PersonBlockItem {...props} />
		)

	return (
		<Block
			className={styles().mod({large: fullwidth})}
			container={container}
			sidebar={sidebar}
		>
			{hasPersons && name && (
				<Theme.H2 className={styles('title')}>{name}</Theme.H2>
			)}
			{singleOrMultiplePersons}
		</Block>
	)
}

export const PersonBlockItem: React.FC<PersonBlockItemType> = ({
	image,
	name,
	title,
	text,
	phone,
	email,
	collaborator
}) => {
	const url = collaborator?.url
	const t = useTranslation('personblock')

	return (
		<div className={styles.item()}>
			<div className={styles.item('row')}>
				{image && image.src && (
					<div className={styles.item('image')}>
						<Link to={url} className={styles.item('image-img')}>
							<Background
								img={image}
								className={styles.item('image-img-bg').toString()}
							/>
						</Link>
					</div>
				)}
				<div className={styles.item('content')}>
					<Theme.H5 className={styles.item('content-title')}>
						<Link to={url}>{name || title}</Link>
					</Theme.H5>
					{text && (
						<div className={styles.item('content-text')}>
							<Theme.Wysiwyg>{text}</Theme.Wysiwyg>
						</div>
					)}

					{email && (
						<Theme.Link
							to={`mailto:${email}`}
							className={styles.item('content-email')}
						>
							{email}
						</Theme.Link>
					)}
					{phone && <p className={styles.item('content-phone')}>{phone}</p>}
					{url && (
						<div className={styles.item('content-collaborator')}>
							<Theme.Link to={collaborator.url}>
								{t.about + '  ' + getFirstWord(name || title)}
							</Theme.Link>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

function getFirstWord(str) {
	const spaceIndex = str.indexOf(' ')
	return spaceIndex === -1 ? str : str.substr(0, spaceIndex)
}
